import { z } from 'zod';
import { UNIDADES } from '../catalogos/observaciones.js';
import { esquemaId, esquemaMotivo } from './comunes.js';
import { esquemaInstante } from './cita.js';
/**
 * Seguimiento: autocaptura, cuestionarios y revision (F4.7, FOLLOW-01, plan §8.5).
 *
 * - Lo que registra la paciente entra con origen `paciente` y queda PENDIENTE de
 *   revision: no se vuelve dato profesional por si solo.
 * - Corregir una captura propia crea una medicion NUEVA que la sustituye; la
 *   original queda `corregida` con su historial (AC-07). Un documento firmado
 *   que uso el valor conserva su copia: no se toca.
 * - Toda captura de la paciente lleva clave de idempotencia: reintentar desde el
 *   telefono no duplica.
 */
export const CODIGOS_DE_AUTOCAPTURA = [
    'peso',
    'cintura',
    'presion-arterial',
    'frecuencia-cardiaca',
    'glucosa-capilar',
    'temperatura',
];
const clave = z.string().trim().min(8).max(100);
export const esquemaAutocaptura = z
    .object({
    codigo: z.enum(CODIGOS_DE_AUTOCAPTURA),
    valor: z.number().finite().optional(),
    componentes: z.record(z.string().trim().min(1).max(40), z.number().finite()).optional(),
    unidad: z.enum(UNIDADES),
    medidaEn: esquemaInstante,
    claveIdempotencia: clave,
    nota: z.string().trim().max(500).default(''),
    /** Si cumple una actividad de medicion de su plan. */
    ocurrenciaId: esquemaId.optional(),
})
    .strict();
export const esquemaCorreccionDeMedicion = z
    .object({
    valor: z.number().finite().optional(),
    componentes: z.record(z.string().trim().min(1).max(40), z.number().finite()).optional(),
    unidad: z.enum(UNIDADES),
    medidaEn: esquemaInstante,
    motivo: esquemaMotivo,
    claveIdempotencia: clave,
})
    .strict();
export const esquemaRevisarMedicion = z
    .object({
    estado: z.enum(['revisada', 'descartada']),
    motivo: esquemaMotivo.optional(),
})
    .strict()
    .refine((r) => r.estado !== 'descartada' || r.motivo !== undefined, {
    message: 'Descartar exige motivo',
    path: ['motivo'],
});
// --- Cuestionarios ---------------------------------------------------------------
export const TIPOS_DE_PREGUNTA = ['texto', 'escala', 'si-no'];
export const esquemaPregunta = z
    .object({
    id: z.string().regex(/^[a-z0-9-]{2,40}$/, 'Identificador en minúsculas, números o guiones'),
    texto: z.string().trim().min(3).max(300),
    tipo: z.enum(TIPOS_DE_PREGUNTA),
    obligatoria: z.boolean().default(true),
})
    .strict();
export const esquemaAsignarCuestionario = z
    .object({
    pacienteId: esquemaId,
    titulo: z.string().trim().min(2).max(160),
    preguntas: z
        .array(esquemaPregunta)
        .min(1)
        .max(30)
        .refine((p) => new Set(p.map((x) => x.id)).size === p.length, {
        message: 'Cada pregunta necesita un identificador distinto',
    }),
})
    .strict();
export const esquemaResponderCuestionario = z
    .object({
    respuestas: z.record(z.string().regex(/^[a-z0-9-]{2,40}$/), z.union([z.string().trim().max(2000), z.number().int().min(0).max(10), z.boolean()])),
})
    .strict();
/** Que las respuestas cuadren con las preguntas: tipo y obligatorias. Pura. */
export function problemasDeRespuestasDeCuestionario(preguntas, respuestas) {
    const problemas = [];
    const ids = new Set(preguntas.map((p) => p.id));
    for (const id of Object.keys(respuestas)) {
        if (!ids.has(id))
            problemas.push(`No hay pregunta «${id}»`);
    }
    for (const pregunta of preguntas) {
        const valor = respuestas[pregunta.id];
        const vacia = valor === undefined || valor === '';
        if (vacia) {
            if (pregunta.obligatoria)
                problemas.push(`Falta responder: ${pregunta.texto}`);
            continue;
        }
        const correcto = (pregunta.tipo === 'texto' && typeof valor === 'string') ||
            (pregunta.tipo === 'escala' && typeof valor === 'number') ||
            (pregunta.tipo === 'si-no' && typeof valor === 'boolean');
        if (!correcto)
            problemas.push(`Respuesta de tipo equivocado: ${pregunta.texto}`);
    }
    return problemas;
}
export const ESTADOS_DE_CUESTIONARIO = [
    'pendiente',
    'respondido',
    'revisado',
    'cancelado',
];
//# sourceMappingURL=seguimiento.js.map